import { useCallback, useEffect, useState } from "react";
import { Mail, RefreshCw, Send, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const STATUS_META = {
  sent: { de: "Gesendet", en: "Sent", cls: "border-emerald-500/50 text-emerald-700 bg-emerald-500/10", icon: CheckCircle2 },
  failed: { de: "Fehlgeschlagen", en: "Failed", cls: "border-red-400/60 text-red-700 bg-red-500/10", icon: AlertTriangle },
  pending_config: { de: "Wartet auf Konfiguration", en: "Awaiting configuration", cls: "border-amber-400/60 text-amber-700 bg-amber-500/10", icon: Clock },
};

export default function AdminEmails() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/emails").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/emails/status").then((r) => setConfig(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const retry = async (id) => {
    try {
      setBusy(true);
      const res = await api.post(`/admin/emails/${id}/retry`);
      if (res.data.status === "sent") toast.success(t("E-Mail gesendet", "Email sent"));
      else if (res.data.status === "pending_config") toast.info(t("E-Mail-Provider noch nicht konfiguriert (.env)", "Email provider still missing (.env)"));
      else toast.error(res.data.error || t("Versand fehlgeschlagen", "Sending failed"));
      load();
    } catch {
      toast.error(t("Fehler", "Error"));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    try {
      setBusy(true);
      const res = await api.post("/admin/emails/test");
      if (res.data.status === "sent") toast.success(t("Testnachricht gesendet", "Test message sent"));
      else toast.info(t("Testnachricht im Postausgang abgelegt \u2013 Versand erfolgt nach Konfiguration", "Test message stored in outbox \u2013 will be sent after configuration"));
      load();
    } catch {
      toast.error(t("Fehler", "Error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-emails-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{t("E-Mail-Postausgang", "Email Outbox")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("System-E-Mails (Formulare, Passwort-Resets) \u2013 Versand \u00fcber Microsoft Graph", "System emails (forms, password resets) \u2013 delivery via Microsoft Graph")}</p>
        </div>
        <Button variant="outline" className="rounded-[10px]" disabled={busy} onClick={sendTest} data-testid="email-send-test">
          <Send className="h-4 w-4 mr-1.5" /> {t("Testnachricht senden", "Send test message")}
        </Button>
      </div>

      {config && (
        <Card className={`rounded-[14px] border ${config.configured ? "border-emerald-500/40" : "border-amber-400/50"}`} data-testid="email-config-banner">
          <CardContent className="p-4 flex flex-wrap items-center gap-3 text-sm">
            {config.configured ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <span>
                  {config.provider === "graph"
                    ? <>{t("Versand aktiv via Microsoft Graph", "Delivery active via Microsoft Graph")} (<b>{config.graph_sender}</b>)</>
                    : <>{t("Exchange-Versand aktiv", "Exchange delivery active")} ({config.host})</>}
                  {" \u2192 "}{t("Benachrichtigungen an", "Notifications to")}: <b>{config.notify_email}</b>
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                <span>
                  {t(
                    "Kein E-Mail-Provider konfiguriert. Bitte Microsoft Graph (GRAPH_TENANT_ID, GRAPH_CLIENT_ID, GRAPH_CLIENT_SECRET, GRAPH_SENDER) oder SMTP-Zugangsdaten in backend/.env eintragen \u2013 Nachrichten werden bis dahin hier gesammelt.",
                    "No email provider configured. Please set Microsoft Graph (GRAPH_TENANT_ID, GRAPH_CLIENT_ID, GRAPH_CLIENT_SECRET, GRAPH_SENDER) or SMTP credentials in backend/.env \u2013 messages are collected here until then."
                  )}{" "}
                  {t("Empf\u00e4nger", "Recipient")}: <b>{config.notify_email || "\u2013"}</b>
                </span>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-[16px] overflow-hidden">
        {!items ? (
          <CardContent className="p-6 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</CardContent>
        ) : items.length === 0 ? (
          <CardContent className="py-16 text-center">
            <Mail className="h-8 w-8 text-muted-foreground/40 mx-auto" />
            <p className="mt-3 text-sm text-muted-foreground">{t("Noch keine Benachrichtigungen. Sie entstehen automatisch bei Formular-Eing\u00e4ngen.", "No notifications yet. They are created automatically on form submissions.")}</p>
          </CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="email-outbox-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("Betreff", "Subject")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("Empf\u00e4nger", "Recipient")}</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>{t("Erstellt", "Created")}</TableHead>
                  <TableHead className="text-right">{t("Aktion", "Action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META.pending_config;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={m.id} className="cursor-pointer" onClick={() => api.get(`/admin/emails/${m.id}`).then((r) => setSelected(r.data)).catch(() => setSelected(m))} data-testid={`email-row-${m.id}`}>
                      <TableCell className="text-sm font-medium max-w-[280px] truncate">{m.subject}</TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{m.to}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-full text-[10px] ${meta.cls}`}>
                          <Icon className="h-3 w-3 mr-1" /> {t(meta.de, meta.en)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono-num text-xs text-muted-foreground">{new Date(m.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        {m.status !== "sent" && (
                          <Button size="sm" variant="outline" className="rounded-[8px] h-8" disabled={busy} onClick={(e) => { e.stopPropagation(); retry(m.id); }} data-testid={`email-retry-${m.id}`}>
                            <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t("Erneut senden", "Retry")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="text-base">{selected.subject}</DialogTitle>
              </DialogHeader>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>{t("An", "To")}: {selected.to} {"\u00b7"} Status: {selected.status} {selected.error ? `\u00b7 ${selected.error}` : ""}</p>
              </div>
              {selected.body_html ? (
                <iframe
                  title="email-preview"
                  sandbox=""
                  srcDoc={selected.body_html}
                  className="w-full h-[420px] rounded-[12px] border border-border bg-white"
                  data-testid="email-preview-html"
                />
              ) : (
                <p className="text-sm">{selected.body_text}</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
